import React, { useEffect, useReducer, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import BotModal from "../../components/BotQueriesModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_BOTS":
      const botsquerry = action.payload;
      const newBots = [];

      botsquerry.forEach((bot) => {
        const botIndex = state.findIndex((q) => q.id === bot.id);
        if (botIndex !== -1) {
          state[botIndex] = bot;
        } else {
          newBots.push(bot);
        }
      });

      return [...state, ...newBots];

    case "UPDATE_BOTS":
      const botsIndex = state.findIndex((u) => u.id === action.payload.id);
      if (botsIndex !== -1) {
        state[botsIndex] = action.payload;
        return [...state];
      } else {
        return [action.payload, ...state];
      }

    case "DELETE_BOTS":
      return state.filter((q) => q.id !== action.payload);

    case "RESET":
      return [];

    default:
      return state;
  }
};

const Bots = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const [botsquerry, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [botsModalOpen, setBotsModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [botType, setBotType] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/botsquerry", { params: { id } });
        dispatch({ type: "LOAD_BOTS", payload: data });

      if (data.length > 0) {
       setBotType(!!data[0].chatbotTypes ? "alertbot" : "typebot");
      }

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("botsquerry", (data) => {
      if (["update", "create"].includes(data.action)) {
        dispatch({ type: "UPDATE_BOTS", payload: data.botsquerry });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_BOTS", payload: data.botId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenQueueModal = (id) => {
    setBotsModalOpen(id);
    setSelectedQueue(id);
  };

  const handleCloseQueueModal = () => {
    setBotsModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (botsquerry, id) => {
    setSelectedQueue(botsquerry, id);
    setBotsModalOpen(botsquerry, id);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (botId) => {
    try {
      await api.delete(`/botsquerry/${botId}`);
      toast.success(i18n.t("Bots deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("botsquerry.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("botsquerry.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <BotModal
        open={botsModalOpen}
        onClose={handleCloseQueueModal}
        botId={selectedQueue?.id}
        id={id}
      />

      <MainHeader>
        <Title>{i18n.t("botsquerry.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenQueueModal(id)}
          >
            {i18n.t("botsquerry.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {botType === "alertbot"
                  ? "Título de alerta"
                  : botType === "typebot"
                  ? "Entrada del usuario"
                  : i18n.t("botsquerry.table.queries")}
              </TableCell>
              <TableCell align="center">
                {botType === "alertbot"
                  ? "Mensaje enviado"
                  : i18n.t("botsquerry.table.replies")}
              </TableCell>
              <TableCell align="center">
                {botType === "alertbot"
                  ? "Requiere número"
                  : i18n.t("botsquerry.table.requerir")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("botsquerry.table.actions")}
              </TableCell>
            </TableRow>

            {botType === "alertbot" && (
                  <TableRow>
      <TableCell colSpan={4}>
        <div className={classes.curlExample}>
          {`
📡 API para enviar alertas:

curl -X POST https://tudominio.com/api/messages/send \\
  -H 'Authorization: Bearer TU_TOKEN' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "number": "593999999999",
    "body": "¡Alerta crítica!",
   "whatsappId": ${botsquerry[0]?.whatsappId ?? 0}

  }'
          `}
        </div>
      </TableCell>
    </TableRow>
            )}
          </TableHead>

          <TableBody>
            <>
              {botsquerry.map((bot) => (
                <TableRow key={bot.id}>
                  <TableCell align="center">{bot.queries}</TableCell>
                  <TableCell align="center">
                    <div className={classes.customTableCell}>
                      <Typography
                        style={{ width: 300, textAlign: "center" }}
                        noWrap
                        variant="body2"
                      >
                        {bot.replyMessage}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell align="center">{bot.requeries}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditQueue(bot, id)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedQueue(bot);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Bots;
